import {
  AccountTypeEnum,
  Country,
  countryOptions,
  formatUsername,
  InputVariantTypes,
  Location,
  MAX_USERNAME_LENGTH,
  MIN_USERNAME_LENGTH,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import Link from "next/link";
import { useState } from "react";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { CUSTOMER_EMPLOYEES, CUSTOMER_REVENUES } from "../consts";
import { FormFooter } from "../FormFooter";
import { Select } from "../Select";
import { TermsAcceptance } from "../TermsAcceptance/TermsAcceptance";
import { TextField } from "../TextField";
import { Typography } from "../Typography";
import {
  FormDataOption,
  transformArrayToFormData,
} from "../utils/transform-array-to-form-data";
import { Wrapper } from "../Wrapper";
import { FullScreenLoader } from "./FullScreenLoader";

const Form = styled.form`
  background: ${({ theme }) => theme.colors.snowdrop};
  border-radius: 8px;
  padding: 40px 32px;
`;

const RowItems = styled.div`
  display: flex;
  flex-wrap: wrap;

  @media ${({ theme }) => theme.bp.tabletUp} {
    gap: 24px;
  }
`;

const RowItem = styled.div`
  width: 100%;
  margin-bottom: 16px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    flex: 1;
    margin-bottom: 22px;
  }
`;

const ButtonContainer = styled.div`
  text-align: center;
  margin: 32px 0 24px;
`;

const SignInText = styled.p`
  font-size: 14px;
  text-align: center;
`;

const SignInLink = styled(Link)`
  color: ${({ theme }) => theme.colors.charcoal};
  margin-left: 4px;
`;

const SectionHeader = styled(Typography)`
  margin-bottom: 16px;
`;

const BusinessCheckboxWrapper = styled.div`
  margin-bottom: 24px;
`;

export type FreeSignupFormProps = {
  location: Location | null;
  createFreeAccount: (args: FreeSignupFormValues) => Promise<unknown>;
  isLoading: boolean;
};

export type FreeSignupFormValues = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
  groupName: string;
  country: Country;
  numemployees: string;
  revenue: string;
  accountType: AccountTypeEnum;
};

export const FreeSignupForm = ({
  location,
  createFreeAccount,
  isLoading,
}: FreeSignupFormProps) => {
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [isEmailTermsChecked, setIsEmailTermsChecked] = useState(false);
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    username: false,
    email: false,
    password: false,
    groupName: false,
    numemployees: false,
    revenue: false,
  });
  const [formValues, setFormValue] = useState<FreeSignupFormValues>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    password: "",
    groupName: "",
    country: location
      ? location.countryCode !== "EU"
        ? location.countryCode
        : "GB"
      : "US",
    numemployees: "",
    revenue: "",
    accountType: AccountTypeEnum.BUSINESS,
  });

  const isIndividual = formValues.accountType === AccountTypeEnum.INDIVIDUAL;

  const businessFieldsValid = isIndividual
    ? true
    : formValues.groupName && formValues.numemployees && formValues.revenue;

  const isUsernameValid =
    formValues.username.length >= MIN_USERNAME_LENGTH &&
    formValues.username.length <= MAX_USERNAME_LENGTH;

  const isButtonDisabled =
    !formValues.firstName ||
    !formValues.lastName ||
    !isUsernameValid ||
    !formValues.email ||
    !formValues.password ||
    formValues.password.length < 8 ||
    !businessFieldsValid ||
    !isTermsChecked ||
    !isEmailTermsChecked ||
    isLoading;

  return (
    <Wrapper>
      {isLoading && <FullScreenLoader />}
      <Form
        onSubmit={async (e) => {
          e.preventDefault();

          if (isButtonDisabled) {
            return;
          }

          await createFreeAccount(formValues);
        }}
      >
        <SectionHeader use="sectionTitle" as="h2">
          Your details
        </SectionHeader>
        <RowItems>
          <RowItem>
            <TextField
              isDisabled={isLoading}
              isInvalid={formValues.firstName.length === 0 && touched.firstName}
              errorText="First name is required"
              label="First name"
              name="firstName"
              onChange={(event) => {
                setFormValue({ ...formValues, firstName: event.target.value });
                setTouched({ ...touched, firstName: false });
              }}
              onBlur={() => {
                setTouched({ ...touched, firstName: true });
              }}
              id="firstName"
              value={formValues.firstName}
            />
          </RowItem>
          <RowItem>
            <TextField
              isDisabled={isLoading}
              errorText="Last name is required"
              isInvalid={formValues.lastName.length === 0 && touched.lastName}
              label="Last name"
              name="lastName"
              onChange={(event) => {
                setFormValue({ ...formValues, lastName: event.target.value });
                setTouched({ ...touched, lastName: false });
              }}
              onBlur={() => {
                setTouched({ ...touched, lastName: true });
              }}
              id="lastName"
              value={formValues.lastName}
            />
          </RowItem>
        </RowItems>
        <RowItems>
          <RowItem>
            <TextField
              isDisabled={isLoading}
              errorText={"Email address is required"}
              isInvalid={formValues.email.length === 0 && touched.email}
              label="Email address"
              name="emailAddress"
              onChange={(event) => {
                setFormValue({ ...formValues, email: event.target.value });
                setTouched({ ...touched, email: false });
              }}
              onBlur={() => {
                setTouched({ ...touched, email: true });
              }}
              id="email"
              value={formValues.email}
              type="email"
            />
          </RowItem>
          <RowItem>
            <TextField
              isDisabled={isLoading}
              isInvalid={formValues.password.length < 8 && touched.password}
              errorText="Your password must be at least 8 characters long"
              label="Password"
              name="password"
              onChange={(event) => {
                setFormValue({ ...formValues, password: event.target.value });
                setTouched({ ...touched, password: false });
              }}
              onBlur={() => {
                setTouched({ ...touched, password: true });
              }}
              id="password"
              value={formValues.password}
              type="password"
            />
          </RowItem>
        </RowItems>
        <RowItems>
          <RowItem>
            <Select
              name="country"
              isDisabled={isLoading}
              label="Country"
              items={countryOptions}
              onChange={(event) => {
                setFormValue({
                  ...formValues,
                  country: event.target.value as Country,
                });
              }}
              id="country"
              data-testid="country-select"
              value={formValues.country}
            />
          </RowItem>
        </RowItems>
        <RowItems>
          <RowItem>
            <TextField
              isDisabled={isLoading}
              isInvalid={!isUsernameValid && touched.username}
              errorText={`Username must be between ${MIN_USERNAME_LENGTH} and ${MAX_USERNAME_LENGTH} characters`}
              label="Username"
              name="username"
              maxLength={MAX_USERNAME_LENGTH}
              onChange={(event) => {
                setFormValue({
                  ...formValues,
                  username: formatUsername(event.target.value),
                });
                setTouched({ ...touched, username: false });
              }}
              onBlur={() => {
                setTouched({ ...touched, username: true });
              }}
              id="username"
              value={formValues.username}
              helpText="Your username will be displayed in your public profile's URL"
            />
          </RowItem>
        </RowItems>

        <BusinessCheckboxWrapper>
          <Checkbox
            label="Sign up as an individual"
            isSelected={isIndividual}
            isDisabled={isLoading}
            onChange={(isSelected) => {
              setFormValue({
                ...formValues,
                accountType: isSelected
                  ? AccountTypeEnum.INDIVIDUAL
                  : AccountTypeEnum.BUSINESS,
                groupName: isSelected ? "" : formValues.groupName,
                numemployees: isSelected ? "" : formValues.numemployees,
                revenue: isSelected ? "" : formValues.revenue,
              });
            }}
          >
            Sign up as an individual
          </Checkbox>
        </BusinessCheckboxWrapper>

        {!isIndividual && (
          <>
            <SectionHeader use="sectionTitle" as="h2">
              Your company details
            </SectionHeader>
            <RowItems>
              <RowItem>
                <TextField
                  isDisabled={isLoading}
                  errorText="Business name is required"
                  isInvalid={
                    formValues.groupName.length === 0 && touched.groupName
                  }
                  label="Business name"
                  name="businessName"
                  maxLength={50}
                  showCharacterCount
                  onChange={(event) => {
                    setFormValue({
                      ...formValues,
                      groupName: event.target.value,
                    });
                    setTouched({ ...touched, groupName: false });
                  }}
                  onBlur={() => {
                    setTouched({ ...touched, groupName: true });
                  }}
                  placeholder="e.g. Green Company"
                  helpText="This will appear on your profile"
                  id="groupName"
                  value={formValues.groupName}
                />
              </RowItem>
            </RowItems>
            <RowItems>
              <RowItem>
                <Select
                  name="numemployees"
                  label="Number of employees"
                  data-testid="employees-select"
                  variant={InputVariantTypes.DEFAULT}
                  isDisabled={isLoading}
                  id="numemployees"
                  items={EmployeesList}
                  onChange={(event) => {
                    setFormValue({
                      ...formValues,
                      numemployees: event.target.value,
                    });
                  }}
                  value={formValues.numemployees}
                />
              </RowItem>
              <RowItem>
                <Select
                  name="revenue"
                  label="Company revenue"
                  data-testid="revenue-select"
                  variant={InputVariantTypes.DEFAULT}
                  isDisabled={isLoading}
                  id="revenue"
                  items={RevenueList}
                  onChange={(event) => {
                    setFormValue({
                      ...formValues,
                      revenue: event.target.value,
                    });
                  }}
                  value={formValues.revenue}
                />
              </RowItem>
            </RowItems>
          </>
        )}
        <TermsAcceptance
          isLoading={isLoading}
          isTermsChecked={isTermsChecked}
          isEmailTermsChecked={isEmailTermsChecked}
          setIsTermsChecked={setIsTermsChecked}
          setIsEmailTermsChecked={setIsEmailTermsChecked}
          disableEmailCheckbox
        />
        <FormFooter withBackground showRecaptchaPolicy />
        <ButtonContainer>
          <Button
            disabled={isButtonDisabled}
            loading={isLoading}
            primary
            blockOnMobile
            submit
            text="Create account"
          />
        </ButtonContainer>
        <SignInText>
          Already registered? <SignInLink href="/api/login">Sign in</SignInLink>
        </SignInText>
      </Form>
    </Wrapper>
  );
};

const EmployeesList: FormDataOption[] =
  transformArrayToFormData(CUSTOMER_EMPLOYEES);

const RevenueList: FormDataOption[] =
  transformArrayToFormData(CUSTOMER_REVENUES);
